/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef } from 'react';

import { useMemoizedFn } from 'ahooks';
import {
  getAuthSnapshot,
  getPendingUnauthorizedGeneration,
  type AxiosRequestConfig,
} from '@adp-arch/bot-http';
import {
  APIErrorEvent,
  handleAPIErrorEvent,
  removeAPIErrorEvent,
} from '@adp-arch/bot-api';

import { checkLoginBase } from '../utils/factory';
import { type UserInfo } from '../types';
import { useUserStore } from '../store/user';

/**
 * Checks login on initialization and listens for invalid-session API errors.
 * When the login status fails, it will be redirected to the login page
 * @param needLogin is required
 * @Param checkLogin Check the specific implementation of login status
 * @Param goLogin Redirect to login page concrete implementation
 */
export const useCheckLoginBase = (
  needLogin: boolean,
  checkLoginImpl: () => Promise<{
    userInfo?: UserInfo;
    hasError?: boolean;
  }>,
  goLogin: () => void,
) => {
  const isSettled = useUserStore(state => state.isSettled);
  const userId = useUserStore(state => state.userInfo?.user_id_str);

  const memoizedGoLogin = useMemoizedFn(goLogin);
  const checkLogin = useMemoizedFn(checkLoginImpl);
  const redirectedGeneration = useRef<number>();
  const goLoginOnce = useMemoizedFn(() => {
    const { generation } = getAuthSnapshot();
    if (redirectedGeneration.current !== generation) {
      redirectedGeneration.current = generation;
      memoizedGoLogin();
    }
  });

  useEffect(() => {
    if (!isSettled) {
      void checkLoginBase(checkLogin);
    }
  }, [isSettled, checkLogin]);

  useEffect(() => {
    // The current page requires login. If the login check result is not logged in, redirect back to the login page.
    if (needLogin && isSettled && !userId) {
      goLoginOnce();
    }
  }, [needLogin, isSettled, userId, goLoginOnce]);

  useEffect(() => {
    const handleUnauthorized = (error: unknown) => {
      const requestGeneration = (error as { config?: AxiosRequestConfig })
        ?.config?.authGeneration;
      if (
        getAuthSnapshot().authenticated === false ||
        (requestGeneration !== undefined &&
          requestGeneration !== getAuthSnapshot().generation)
      ) {
        return;
      }
      useUserStore.getState().reset();
      globalThis.localStorage?.removeItem?.('coze_current_uid');
    };
    // This function is triggered when the Ajax request backend interface appears not authorized/logged in
    handleAPIErrorEvent(APIErrorEvent.UNAUTHORIZED, handleUnauthorized);
    // The transport may see a 401 before any account hook subscribes.
    const pendingGeneration = getPendingUnauthorizedGeneration();
    if (pendingGeneration !== undefined) {
      handleUnauthorized({ config: { authGeneration: pendingGeneration } });
    }
    return () => {
      removeAPIErrorEvent(APIErrorEvent.UNAUTHORIZED, handleUnauthorized);
    };
  }, []);
};
